import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  Skeleton,
  Alert,
  Button,
  Chip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Collapse,
  TextField,
  InputAdornment,
  Pagination,
  IconButton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import BookIcon from '@mui/icons-material/Book';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LoginIcon from '@mui/icons-material/Login';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarIcon from '@mui/icons-material/Star';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import { getGuestProfile, getGuestBookings, getGuestStorageKey, cancelGuestBooking } from './nexastayApi';
import { formatCountryLabel } from '../utils/countryDisplay';

const STATUS_COLORS = {
  confirmed: 'success',
  checked_in: 'info',
  checked_out: 'default',
  cancelled: 'error',
  no_show: 'warning',
  reserved: 'primary',
  pending: 'warning',
};

const PAYMENT_METHOD_LABELS = {
  pay_at_hotel: 'Pay at hotel',
  partial_pay: 'Partial pay online',
  full_pay: 'Full pay online',
};

const PAYMENT_STATUS_META = {
  paid_full: { label: 'Paid in full', color: 'success' },
  partial: { label: 'Partially paid', color: 'warning' },
  pay_at_hotel: { label: 'Due at check-in', color: 'info' },
  unpaid: { label: 'Unpaid', color: 'default' },
};

const PAGE_SIZE = 10;

/** VIP tier from booking count — matches Customer Center detail page. */
function getCustomerTypeTier(bookingCount) {
  const n = Number(bookingCount) || 0;
  if (n > 10) {
    return {
      label: 'VVVIP',
      icon: DiamondIcon,
      bg: 'linear-gradient(135deg, #c084fc 0%, #7c3aed 50%, #4c1d95 100%)',
      shadow: '0 4px 14px rgba(147,51,234,0.45)',
    };
  }
  if (n >= 7) {
    return {
      label: 'VVIP',
      icon: WorkspacePremiumIcon,
      bg: 'linear-gradient(135deg, #fde047 0%, #eab308 50%, #a16207 100%)',
      shadow: '0 4px 14px rgba(202,138,4,0.4)',
    };
  }
  if (n >= 4) {
    return {
      label: 'Gold VIP',
      icon: StarIcon,
      bg: 'linear-gradient(135deg, #fb923c 0%, #ea580c 50%, #c2410c 100%)',
      shadow: '0 4px 14px rgba(234,88,12,0.35)',
    };
  }
  if (n >= 2) {
    return {
      label: 'Gold',
      icon: StarIcon,
      bg: 'linear-gradient(135deg, #fdba74 0%, #f59e0b 50%, #d97706 100%)',
      shadow: '0 4px 14px rgba(245,158,11,0.35)',
    };
  }
  return null;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatCurrency(amount, curr) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: curr || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
}

function formatPhoneWithDialCode(phone, countryCode) {
  const digits = String(phone || '').trim();
  if (!digits) return '';
  const dial = String(countryCode || '').trim() || '+91';
  if (digits.startsWith('+')) return digits;
  return `${dial} ${digits}`;
}

function buildBookingSearchText(booking) {
  const payment = booking.payment || {};
  const guests = Array.isArray(booking.guests) ? booking.guests : [];
  const parts = [
    booking.code,
    booking.status,
    booking.check_in_date,
    booking.check_out_date,
    formatDate(booking.check_in_date),
    formatDate(booking.check_out_date),
    String(booking.total_amount ?? ''),
    String(booking.pax_adults ?? ''),
    String(booking.pax_children ?? ''),
    ...(Array.isArray(booking.room_types) ? booking.room_types : []),
    PAYMENT_METHOD_LABELS[payment.method] || payment.method,
    PAYMENT_STATUS_META[payment.status]?.label,
    ...guests.flatMap((g) => [g.name, g.email, g.phone]),
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function matchesBookingSearch(booking, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return buildBookingSearchText(booking).includes(q);
}

function BookingCard({
  booking,
  theme,
  currency,
  isDark,
  accent,
  canCancel,
  cancellingId,
  onCancelClick,
  expanded,
  onToggle,
}) {
  const payment = booking.payment || {};
  const amountPaid = Number(payment.amount_paid || 0);
  const balanceDue = Number(payment.balance_due ?? Math.max(0, Number(booking.total_amount || 0) - amountPaid));
  const totalAmount = Number(booking.total_amount || 0);
  const bookingCurrency = booking.currency || currency;
  const paymentMethod = payment.method || 'pay_at_hotel';
  const paymentStatus = PAYMENT_STATUS_META[payment.status] || PAYMENT_STATUS_META.unpaid;
  const statusKey = (booking.status || 'confirmed').toLowerCase();
  const roomTypes = Array.isArray(booking.room_types) ? booking.room_types : [];
  const linkedGuests = Array.isArray(booking.guests) ? booking.guests : [];
  const guestPreview = linkedGuests.length > 0
    ? linkedGuests.slice(0, 2).map((g) => g.name).filter(Boolean).join(', ')
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        border: `1px solid ${expanded ? alpha(accent, 0.35) : isDark ? alpha(accent, 0.14) : alpha(accent, 0.18)}`,
        bgcolor: isDark ? 'rgba(0,0,0,0.22)' : theme.paper,
        boxShadow: expanded
          ? `0 12px 36px ${alpha(accent, 0.14)}`
          : `0 4px 16px ${alpha('#000', isDark ? 0.2 : 0.04)}`,
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          px: { xs: 1.75, sm: 2.25 },
          py: 1.5,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderLeft: `4px solid ${accent}`,
          bgcolor: expanded ? alpha(accent, isDark ? 0.08 : 0.05) : 'transparent',
          transition: 'background-color 0.25s ease',
          '&:hover': { bgcolor: alpha(accent, isDark ? 0.1 : 0.06) },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, fontFamily: 'monospace', color: theme.textPrimary, letterSpacing: 0.2 }}
            >
              {booking.code}
            </Typography>
            <Chip
              label={(booking.status || 'confirmed').replace(/_/g, ' ')}
              color={STATUS_COLORS[statusKey] || 'default'}
              size="small"
              sx={{ textTransform: 'capitalize', fontWeight: 700, height: 22, fontSize: '0.68rem' }}
            />
            <Chip
              label={paymentStatus.label}
              color={paymentStatus.color}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, height: 22, fontSize: '0.68rem' }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
            <CalendarMonthIcon sx={{ fontSize: 16, color: accent }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.textSecondary }}>
              {formatDate(booking.check_in_date)}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: theme.textMuted }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.textSecondary }}>
              {formatDate(booking.check_out_date)}
            </Typography>
            {booking.nights > 0 ? (
              <Typography variant="caption" sx={{ color: theme.textMuted }}>
                · {booking.nights} night{booking.nights !== 1 ? 's' : ''}
              </Typography>
            ) : null}
            {guestPreview ? (
              <Typography variant="caption" sx={{ color: theme.textMuted, display: { xs: 'none', md: 'inline' } }}>
                · {guestPreview}{linkedGuests.length > 2 ? ` +${linkedGuests.length - 2}` : ''}
              </Typography>
            ) : null}
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: accent, whiteSpace: 'nowrap' }}>
            {formatCurrency(totalAmount, bookingCurrency)}
          </Typography>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse booking details' : 'Expand booking details'}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{
              color: accent,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.25s ease',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: alpha(accent, 0.12) }} />
        <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: 2 }}>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                  }}
                >
                  <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 600 }}>
                    Occupancy
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                    <Chip
                      size="small"
                      label={`${booking.pax_adults || 1} adult${(booking.pax_adults || 1) !== 1 ? 's' : ''}`}
                      sx={{ height: 22, fontWeight: 600 }}
                    />
                    {(booking.pax_children || 0) > 0 ? (
                      <Chip
                        size="small"
                        icon={<ChildCareIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${booking.pax_children} child${booking.pax_children !== 1 ? 'ren' : ''}`}
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                  }}
                >
                  <HotelOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 600 }}>
                    Rooms
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.textPrimary }}>
                    {booking.total_rooms || 1} room{(booking.total_rooms || 1) !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {roomTypes.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {roomTypes.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    border: `1px solid ${theme.border}`,
                  }}
                />
              ))}
            </Stack>
          ) : null}

          {linkedGuests.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.75,
                mb: 2,
                borderRadius: 2,
                bgcolor: isDark ? 'rgba(0,0,0,0.2)' : alpha(accent, 0.03),
                borderColor: alpha(accent, 0.16),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                <GroupsOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.textPrimary }}>
                  Guest{linkedGuests.length !== 1 ? 's' : ''}
                </Typography>
                <Chip
                  size="small"
                  label={linkedGuests.length}
                  sx={{ ml: 'auto', height: 22, fontWeight: 700, bgcolor: alpha(accent, 0.12), color: accent }}
                />
              </Stack>
              <Stack spacing={1}>
                {linkedGuests.map((guest) => (
                  <Box
                    key={guest.id || guest.name}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)',
                      border: `1px solid ${theme.border}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.textPrimary }}>
                        {guest.name || 'Guest'}
                      </Typography>
                      {guest.is_primary ? (
                        <Chip label="Primary" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      ) : null}
                    </Stack>
                    <Stack spacing={0.35} sx={{ mt: 0.75 }}>
                      {guest.email ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <EmailIcon sx={{ fontSize: 14, color: accent }} />
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>{guest.email}</Typography>
                        </Stack>
                      ) : null}
                      {guest.phone ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <PhoneIcon sx={{ fontSize: 14, color: accent }} />
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>{guest.phone}</Typography>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ) : null}

          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: 2,
              bgcolor: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.02)',
              borderColor: alpha(accent, 0.18),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
              <PaymentsOutlinedIcon sx={{ fontSize: 18, color: accent }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.textPrimary }}>
                Payment
              </Typography>
              <Chip
                size="small"
                icon={
                  paymentMethod === 'pay_at_hotel' ? (
                    <HotelOutlinedIcon sx={{ fontSize: '14px !important' }} />
                  ) : (
                    <CreditCardOutlinedIcon sx={{ fontSize: '14px !important' }} />
                  )
                }
                label={PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod.replace(/_/g, ' ')}
                sx={{ ml: 'auto', height: 22, fontWeight: 600, bgcolor: alpha(accent, 0.1), color: accent }}
              />
            </Stack>
            <Divider sx={{ mb: 1.25, borderColor: theme.border }} />
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: theme.textSecondary }}>Total amount</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatCurrency(totalAmount, bookingCurrency)}</Typography>
              </Stack>
              {amountPaid > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: theme.textSecondary }}>Paid online</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: accent }}>{formatCurrency(amountPaid, bookingCurrency)}</Typography>
                </Stack>
              ) : null}
              {balanceDue > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                    {paymentMethod === 'pay_at_hotel' ? 'Due at check-in' : 'Balance at hotel'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(balanceDue, bookingCurrency)}</Typography>
                </Stack>
              ) : payment.is_paid || payment.status === 'paid_full' ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: theme.textSecondary }}>Balance</Typography>
                  <Chip size="small" label="Nothing due" color="success" variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          {canCancel ? (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={cancellingId === booking.id ? null : <CancelIcon />}
                disabled={cancellingId !== null}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelClick(booking);
                }}
                sx={{ fontWeight: 600 }}
              >
                {cancellingId === booking.id ? 'Cancelling…' : 'Cancel booking'}
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function BookingsPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const location = useLocation();
  const basePath = useNexaStayBasePath();
  const theme = useNexaStayTheme();
  const accent = theme.accent;

  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [cancelConfirmBooking, setCancelConfirmBooking] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const bookingSuccess = location.state?.bookingSuccess;
  const bookingCode = location.state?.bookingCode;

  useEffect(() => {
    if (!hotelId) {
      setLoading(false);
      setError('Hotel not found.');
      return;
    }
    let cancelled = false;
    const guestId = typeof window !== 'undefined' ? localStorage.getItem(getGuestStorageKey(hotelId)) : null;
    if (!guestId) {
      setLoading(false);
      setError('not_logged_in');
      return;
    }
    getGuestProfile(hotelId, guestId)
      .then((data) => { if (!cancelled) setProfile(data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load profile.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hotelId]);

  const fetchBookings = React.useCallback(() => {
    if (!hotelId || !profile?.id) return;
    setBookingsLoading(true);
    getGuestBookings(hotelId, profile.id)
      .then((res) => setBookings(res?.bookings || []))
      .catch(() => setBookings([]))
      .finally(() => setBookingsLoading(false));
  }, [hotelId, profile?.id]);

  useEffect(() => {
    if (!hotelId || !profile?.id) return;
    let cancelled = false;
    setBookingsLoading(true);
    getGuestBookings(hotelId, profile.id)
      .then((res) => { if (!cancelled) setBookings(res?.bookings || []); })
      .catch(() => { if (!cancelled) setBookings([]); })
      .finally(() => { if (!cancelled) setBookingsLoading(false); });
    return () => { cancelled = true; };
  }, [hotelId, profile?.id]);

  const handleCancelBooking = async (bookingId) => {
    if (!hotelId || !profile?.id) return;
    setCancelError(null);
    setCancellingId(bookingId);
    try {
      await cancelGuestBooking(hotelId, profile.id, bookingId);
      setCancelConfirmBooking(null);
      fetchBookings();
    } catch (e) {
      setCancelError(e.response?.data?.detail || 'Failed to cancel booking.');
    } finally {
      setCancellingId(null);
    }
  };

  const canCancelBooking = (b) => {
    const s = (b.status || '').toLowerCase();
    return ['confirmed', 'reserved', 'pending'].includes(s);
  };

  const filteredBookings = useMemo(
    () => bookings.filter((b) => matchesBookingSearch(b, searchQuery)),
    [bookings, searchQuery]
  );

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredBookings.slice(start, start + PAGE_SIZE);
  }, [filteredBookings, page]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleExpanded = (bookingId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 4, px: 2 }}>
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', p: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={72} height={72} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Skeleton variant="text" width="70%" height={36} sx={{ mb: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width={48} height={40} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={90} height={20} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width={80} height={32} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
              </Box>
            </Box>
          </Paper>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1.5 }} />
                  <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1.5 }} />
                  <Skeleton variant="text" width="85%" height={48} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  if (error === 'not_logged_in') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 8, px: 2, color: theme.textPrimary }}>
        <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}20`,
              color: accent,
            }}
          >
            <LoginIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>My Bookings</Typography>
          <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 3 }}>
            Please log in to see your bookings at this hotel.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Button
              component={Link}
              to={nexastayPath(basePath, hotelId)}
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={{
                borderColor: theme.borderAccent,
                color: theme.textPrimary,
                '&:hover': { borderColor: accent, bgcolor: `${accent}12`, color: accent },
              }}
            >
              Back to home
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, py: 4, px: 2 }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, bgcolor: theme.paper }}>{error || 'Profile not found.'}</Alert>
          <Button component={Link} to={nexastayPath(basePath, hotelId)} sx={{ color: accent }}>Back to home</Button>
        </Box>
      </Box>
    );
  }

  const isDark = theme.mode === 'dark';
  const addressParts = [
    profile.address_line1,
    profile.city,
    profile.state,
    profile.country ? formatCountryLabel(profile.country) : '',
    profile.pincode,
  ].filter(Boolean);
  const totalBookings = profile.total_bookings ?? 0;
  const totalSpent = profile.total_spent ?? 0;
  const currency = profile.currency || 'INR';
  const loyaltyMemberNo = String(profile.loyalty_member_no || '').trim();
  const isLoyaltyMember = Boolean(loyaltyMemberNo);
  const customerTypeTier = getCustomerTypeTier(totalBookings);
  const loyaltyProgramTier = profile.loyalty_tier?.display_name || profile.loyalty_tier?.tier_name || null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.bg, pb: 6, color: theme.textPrimary }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 2 }}>
        {bookingSuccess && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {bookingCode ? `Booking ${bookingCode} confirmed successfully.` : 'Booking confirmed successfully.'}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            background: isDark
              ? `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 50%, #2a2a31 100%)`
              : `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.05)} 50%, #fff 100%)`,
            border: `1px solid ${isDark ? alpha(accent, 0.2) : alpha(accent, 0.25)}`,
            boxShadow: `0 4px 20px ${alpha(accent, 0.15)}`,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${accent} 0%, #3ab896 100%)`,
                  boxShadow: `0 4px 16px ${alpha(accent, 0.4)}`,
                  border: `3px solid ${isDark ? alpha(accent, 0.3) : alpha(accent, 0.5)}`,
                }}
              >
                <PersonIcon sx={{ fontSize: 36, color: '#000' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#1a1a1a' }}>
                    {profile.full_name || 'Guest'}
                  </Typography>
                  {customerTypeTier ? (() => {
                    const TierIcon = customerTypeTier.icon;
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          background: customerTypeTier.bg,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          letterSpacing: '0.04em',
                          boxShadow: customerTypeTier.shadow,
                          border: '1px solid rgba(255,255,255,0.35)',
                          textTransform: 'uppercase',
                        }}
                      >
                        <TierIcon sx={{ fontSize: 16 }} />
                        {customerTypeTier.label}
                      </Box>
                    );
                  })() : null}
                  {loyaltyProgramTier && !customerTypeTier ? (
                    <Chip
                      size="small"
                      icon={<WorkspacePremiumIcon sx={{ fontSize: '14px !important' }} />}
                      label={loyaltyProgramTier}
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        bgcolor: alpha(accent, 0.14),
                        color: accent,
                        border: `1px solid ${alpha(accent, 0.3)}`,
                      }}
                    />
                  ) : null}
                </Stack>
                {isLoyaltyMember ? (
                  <Chip
                    icon={<CardMembershipIcon sx={{ fontSize: 14, color: '#000 !important' }} />}
                    label={`Loyalty ID: ${loyaltyMemberNo}`}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: '#000',
                      fontWeight: 600,
                      fontSize: '0.6875rem',
                      height: 24,
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(217, 119, 6, 0.35)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      '& .MuiChip-icon': { color: '#000', ml: '6px' },
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <ReceiptLongIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: accent }}>{totalBookings}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666' }}>Total Bookings</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <PaidIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: accent }}>
                      {totalSpent > 0 ? formatCurrency(totalSpent, currency) : '—'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666' }}>Total Spent</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                backgroundColor: isDark ? '#2a2a31' : theme.paper,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#ddd' : '#333', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon sx={{ color: accent, fontSize: 22 }} />
                  Contact & Info
                </Typography>
                {profile.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <EmailIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>{profile.email}</Typography>
                  </Box>
                )}
                {profile.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 20, color: accent }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>
                      {formatPhoneWithDialCode(profile.phone, profile.country_code)}
                    </Typography>
                  </Box>
                )}
                {addressParts.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <LocationOnIcon sx={{ fontSize: 20, color: accent, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555' }}>{addressParts.join(', ')}</Typography>
                    </Box>
                  </Box>
                )}
                {!profile.email && !profile.phone && addressParts.length === 0 && (
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666' }}>No contact details on file.</Typography>
                )}
                {(isLoyaltyMember || customerTypeTier || loyaltyProgramTier) ? (
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                    <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 700, display: 'block', mb: 1 }}>
                      Membership
                    </Typography>
                    {customerTypeTier ? (
                      <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600, mb: 0.75 }}>
                        Customer type: {customerTypeTier.label}
                      </Typography>
                    ) : loyaltyProgramTier ? (
                      <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600, mb: 0.75 }}>
                        Tier: {loyaltyProgramTier}
                      </Typography>
                    ) : null}
                    {isLoyaltyMember ? (
                      <Typography variant="body2" sx={{ color: isDark ? '#ccc' : '#555', fontFamily: 'monospace' }}>
                        {loyaltyMemberNo}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </CardContent>
            </Card>

            <Button
              component={Link}
              to={nexastayPath(basePath, hotelId)}
              variant="outlined"
              startIcon={<HomeIcon />}
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.borderAccent,
                color: theme.textPrimary,
                fontWeight: 600,
                '&:hover': { borderColor: accent, bgcolor: `${accent}12`, color: accent },
              }}
            >
              Back to home
            </Button>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${isDark ? alpha(accent, 0.15) : alpha(accent, 0.2)}`,
                backgroundColor: isDark ? '#2a2a31' : theme.paper,
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha('#000', isDark ? 0.2 : 0.05)}`,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: `1px solid ${isDark ? '#333' : alpha(accent, 0.12)}`,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, transparent 100%)`
                    : `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, transparent 100%)`,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <BookIcon sx={{ color: accent, fontSize: 24 }} />
                    My Bookings
                    {!bookingsLoading && bookings.length > 0 ? (
                      <Chip
                        label={searchQuery.trim() ? `${filteredBookings.length} / ${bookings.length}` : bookings.length}
                        size="small"
                        sx={{
                          ml: 0.5,
                          height: 22,
                          fontWeight: 700,
                          bgcolor: alpha(accent, 0.15),
                          color: accent,
                        }}
                      />
                    ) : null}
                  </Typography>
                  {!bookingsLoading && bookings.length > 0 ? (
                    <TextField
                      size="small"
                      placeholder="Search booking ID, dates, guest, status…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 20, color: theme.textMuted }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: { xs: '100%', sm: 280 },
                        maxWidth: 360,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                          '& fieldset': { borderColor: alpha(accent, 0.2) },
                          '&:hover fieldset': { borderColor: alpha(accent, 0.35) },
                          '&.Mui-focused fieldset': { borderColor: accent },
                        },
                      }}
                    />
                  ) : null}
                </Stack>
              </Box>
              <CardContent sx={{ '&:last-child': { pb: 2.5 }, pt: 2.5 }}>
                {bookingsLoading && (
                  <Stack spacing={1.5}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 2.5 }} />
                    ))}
                  </Stack>
                )}
                {!bookingsLoading && (!bookings || bookings.length === 0) && (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <ReceiptLongIcon sx={{ fontSize: 48, color: alpha(accent, 0.35), mb: 1.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: theme.textPrimary, mb: 0.5 }}>
                      No bookings yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 2 }}>
                      Book a room from the home page to get started.
                    </Typography>
                    <Button
                      component={Link}
                      to={nexastayPath(basePath, hotelId)}
                      variant="contained"
                      sx={{
                        bgcolor: accent,
                        color: isDark ? '#121212' : '#000',
                        fontWeight: 700,
                        '&:hover': { bgcolor: accent },
                      }}
                    >
                      Browse rooms
                    </Button>
                  </Box>
                )}
                {cancelError && (
                  <Alert severity="error" onClose={() => setCancelError(null)} sx={{ mb: 2, borderRadius: 2 }}>
                    {cancelError}
                  </Alert>
                )}
                {!bookingsLoading && bookings.length > 0 && filteredBookings.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <SearchIcon sx={{ fontSize: 40, color: alpha(accent, 0.35), mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: theme.textPrimary, mb: 0.5 }}>
                      No matching bookings
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 2 }}>
                      Try a different booking ID, date, guest name, or status.
                    </Typography>
                    <Button variant="outlined" onClick={() => setSearchQuery('')} sx={{ borderColor: theme.border }}>
                      Clear search
                    </Button>
                  </Box>
                )}
                {!bookingsLoading && paginatedBookings.length > 0 && (
                  <>
                    <Stack spacing={1.25}>
                      {paginatedBookings.map((b) => (
                        <BookingCard
                          key={b.id}
                          booking={b}
                          theme={theme}
                          currency={currency}
                          isDark={isDark}
                          accent={accent}
                          canCancel={canCancelBooking(b)}
                          cancellingId={cancellingId}
                          onCancelClick={setCancelConfirmBooking}
                          expanded={expandedIds.has(b.id)}
                          onToggle={() => toggleExpanded(b.id)}
                        />
                      ))}
                    </Stack>
                    {filteredBookings.length > PAGE_SIZE ? (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1.5}
                        sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${theme.border}` }}
                      >
                        <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 600 }}>
                          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredBookings.length)} of {filteredBookings.length}
                        </Typography>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                          color="primary"
                          shape="rounded"
                          size="small"
                          sx={{
                            '& .MuiPaginationItem-root': {
                              fontWeight: 600,
                              color: theme.textPrimary,
                            },
                            '& .Mui-selected': {
                              bgcolor: `${accent} !important`,
                              color: `${isDark ? '#121212' : '#000'} !important`,
                            },
                          }}
                        />
                      </Stack>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={Boolean(cancelConfirmBooking)}
          onClose={() => !cancellingId && setCancelConfirmBooking(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: isDark ? '#2a2a31' : theme.paper,
              border: `1px solid ${isDark ? '#444' : '#eee'}`,
            },
          }}
        >
          <DialogTitle sx={{ color: theme.textPrimary, fontWeight: 600 }}>
            Cancel booking?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: theme.textSecondary }}>
              {cancelConfirmBooking && (
                <>
                  Are you sure you want to cancel booking <strong>{cancelConfirmBooking.code}</strong>?
                  {cancelConfirmBooking.check_in_date && (
                    <> (Check-in: {formatDate(cancelConfirmBooking.check_in_date)})</>
                  )}
                  {' '}This action cannot be undone.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setCancelConfirmBooking(null)}
              disabled={cancellingId !== null}
              sx={{ color: theme.textSecondary }}
            >
              Keep booking
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => cancelConfirmBooking && handleCancelBooking(cancelConfirmBooking.id)}
              disabled={cancellingId !== null}
              startIcon={cancellingId === cancelConfirmBooking?.id ? null : <CancelIcon />}
            >
              {cancellingId === cancelConfirmBooking?.id ? 'Cancelling…' : 'Yes, cancel booking'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
